import { Link } from "@/lib/nav";
import { useState } from "react";
import { AdminGate } from "@/components/AdminGate";
import { BoardHeader, BoardShell, Loading, SectionTitle } from "@/components/Chrome";
import { fmtDate, fmtTime, toLocalInput } from "@/lib/format";
import type { BoardFlight } from "@/lib/store";
import { createFlight, deleteFlight, setFlightStatus, useBoard } from "@/lib/store";
import { flightCapacity } from "@/lib/types";

function defaultDepart(): string {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  d.setHours(18, 30, 0, 0);
  return toLocalInput(d.toISOString());
}

function codeFromDate(localValue: string): string {
  const d = new Date(localValue);
  if (isNaN(d.getTime())) return "DN-0000";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `DN-${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

/** 一種桌型：count 桌，每桌 seats 位 */
interface TableGroup {
  count: number;
  seats: number;
  vip: boolean;
}

/**
 * 這一組桌子會拿到的桌號範圍，例如「第 1–3 桌」。
 * 主辦人需要知道哪幾桌是大桌才能跟餐廳對得起來。
 */
function tableRangeLabel(groups: TableGroup[], index: number): string {
  const start = groups.slice(0, index).reduce((n, g) => n + g.count, 0) + 1;
  const end = start + groups[index].count - 1;
  return start === end ? `第 ${start} 桌` : `第 ${start}–${end} 桌`;
}

function L({ children }: { children: React.ReactNode }) {
  return <span className="text-sub mb-1.5 block text-[13px] font-medium">{children}</span>;
}

function CreateFlightForm() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [departLocal, setDepartLocal] = useState(defaultDepart);
  const [endLocal, setEndLocal] = useState("");
  const [code, setCode] = useState(() => codeFromDate(defaultDepart()));
  const [codeTouched, setCodeTouched] = useState(false);
  const [venueName, setVenueName] = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [gate, setGate] = useState("");
  const [origin, setOrigin] = useState("OFFICE");
  const [boardingMinutes, setBoardingMinutes] = useState(30);
  const [groups, setGroups] = useState<TableGroup[]>([{ count: 6, seats: 10, vip: false }]);
  const [inviteOnly, setInviteOnlyChecked] = useState(false);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const capacity = groups.reduce((n, g) => n + g.count * g.seats, 0);
  const tableTotal = groups.reduce((n, g) => n + g.count, 0);

  const patchGroup = (i: number, patch: Partial<TableGroup>) =>
    setGroups(groups.map((g, j) => (j === i ? { ...g, ...patch } : g)));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (busy || !title.trim() || !venueName.trim() || !code.trim()) return;
    const depart = new Date(departLocal);
    if (isNaN(depart.getTime())) {
      setError("請選擇有效的日期時間。");
      return;
    }
    const end = endLocal ? new Date(endLocal) : null;
    if (end && (isNaN(end.getTime()) || end <= depart)) {
      setError("結束時間需要晚於開始時間。");
      return;
    }
    setBusy(true);
    const res = await createFlight({
      code: code.trim(),
      title: title.trim(),
      departAt: depart.toISOString(),
      endAt: end ? end.toISOString() : undefined,
      boardingMinutes,
      origin: origin.trim() || "OFFICE",
      venueName: venueName.trim(),
      venueAddress: venueAddress.trim(),
      gate: gate.trim() || "現場公告",
      tableGroups: groups.map((g) => ({
        count: Math.max(1, Math.min(60, Math.round(g.count) || 1)),
        seats: Math.max(2, Math.min(20, Math.round(g.seats) || 2)),
        vip: g.vip,
      })),
      inviteOnly,
      notes: notes.trim() || undefined,
    });
    setBusy(false);
    if (!res.ok) {
      setError(
        res.error === "code-exists"
          ? `活動代號 ${code.trim().toUpperCase()} 已存在，請換一個。`
          : "建立失敗，請再試一次。",
      );
      return;
    }
    setCreatedCode(res.flight.code);
    setTitle("");
    setVenueName("");
    setVenueAddress("");
    setGate("");
    setNotes("");
  };

  const numField = (value: number, set: (n: number) => void, min: number, max: number) => (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => set(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
      className="field-input"
    />
  );

  return (
    <div className="card mt-6 p-6">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left">
        <span className="text-[17px] font-bold">＋ 開新活動</span>
        <span className="text-sub">{open ? "收合 ▲" : "展開 ▼"}</span>
      </button>

      {open && (
        <form onSubmit={submit} className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <L>活動名稱 *</L>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="field-input" placeholder="2026 年終尾牙" required />
          </label>

          <label className="block">
            <L>日期時間（開席）*</L>
            <input
              type="datetime-local"
              value={departLocal}
              onChange={(e) => {
                setDepartLocal(e.target.value);
                if (!codeTouched) setCode(codeFromDate(e.target.value));
              }}
              className="field-input"
              required
            />
          </label>
          <label className="block">
            <L>結束時間（選填）</L>
            <input
              type="datetime-local"
              value={endLocal}
              onChange={(e) => setEndLocal(e.target.value)}
              className="field-input"
            />
          </label>

          <label className="block">
            <L>活動代號 *</L>
            <input
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setCodeTouched(true);
              }}
              className="field-input"
              placeholder="DN-1231"
              required
            />
          </label>

          <label className="block">
            <L>餐廳名稱 *</L>
            <input value={venueName} onChange={(e) => setVenueName(e.target.value)} className="field-input" placeholder="饗宴會館" required />
          </label>
          <label className="block">
            <L>餐廳地址</L>
            <input value={venueAddress} onChange={(e) => setVenueAddress(e.target.value)} className="field-input" placeholder="台北市…" />
          </label>

          <label className="block">
            <L>登機門（樓層 / 包廂）</L>
            <input value={gate} onChange={(e) => setGate(e.target.value)} className="field-input" placeholder="2F 宴會廳" />
          </label>
          <label className="block">
            <L>出發地顯示文字</L>
            <input value={origin} onChange={(e) => setOrigin(e.target.value)} className="field-input" placeholder="OFFICE" />
          </label>

          <label className="block">
            <L>提前入場分鐘數</L>
            {numField(boardingMinutes, setBoardingMinutes, 0, 180)}
          </label>

          {/*
            桌型群組：餐廳的桌子常常不是統一大小（有些 8 人、有些 4 人），
            所以用「幾桌 × 幾位」一列一種桌型，而不是單一的每桌人數。
            桌號會依照這裡的順序連號編下去。
          */}
          <div className="sm:col-span-2">
            <L>桌型（可以有多種大小的桌子）</L>
            <div className="mt-1.5 flex flex-col gap-2">
              {groups.map((g, i) => (
                <div key={i} className="rounded-xl border border-line bg-card px-3.5 py-3">
                  {/* 上排：這組對應到第幾桌 + VIP 與移除 */}
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <span className="text-[13px] font-semibold">{tableRangeLabel(groups, i)}</span>
                    <div className="flex items-center gap-3">
                      <label className="flex cursor-pointer items-center gap-1.5 text-[13px]">
                        <input
                          type="checkbox"
                          checked={g.vip}
                          onChange={(e) => patchGroup(i, { vip: e.target.checked })}
                          className="h-4 w-4 accent-[#b8892e]"
                        />
                        VIP 主桌
                      </label>
                      {groups.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setGroups(groups.filter((_, j) => j !== i))}
                          className="text-bad-deep text-[13px] font-medium"
                          aria-label={`刪除${tableRangeLabel(groups, i)}這組桌型`}
                        >
                          移除
                        </button>
                      )}
                    </div>
                  </div>

                  {/* 下排：幾桌 × 幾位 = 幾席 */}
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={g.count}
                      onChange={(e) => patchGroup(i, { count: Number(e.target.value) })}
                      className="field-input w-20 px-2 text-center"
                      aria-label={`${tableRangeLabel(groups, i)}的桌數`}
                    />
                    <span className="text-sub shrink-0 text-[14px]">桌 ×</span>
                    <input
                      type="number"
                      min={2}
                      max={20}
                      value={g.seats}
                      onChange={(e) => patchGroup(i, { seats: Number(e.target.value) })}
                      className="field-input w-20 px-2 text-center"
                      aria-label={`${tableRangeLabel(groups, i)}的每桌座位數`}
                    />
                    <span className="text-sub shrink-0 text-[14px]">位</span>
                    <span className="text-sub ml-auto shrink-0 text-[13px] tabular-nums">
                      ＝ {g.count * g.seats} 席
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setGroups([...groups, { count: 1, seats: 4, vip: false }])}
              className="btn btn-secondary mt-2 px-3.5 py-2 text-[13px]"
            >
              ＋ 加一種桌型
            </button>
          </div>

          <label className="block sm:col-span-2">
            <L>備註（顯示在活動頁）</L>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="field-input" placeholder="注意事項…" />
          </label>

          <label className="flex items-center gap-2.5 sm:col-span-2">
            <input
              type="checkbox"
              checked={inviteOnly}
              onChange={(e) => setInviteOnlyChecked(e.target.checked)}
              className="h-4 w-4 accent-[#007aff]"
            />
            <span className="text-[14px]">
              接龍名單制——只有匯入名單上的名字能報名（建立後到「乘客名單」頁貼上 LINE 接龍）
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
            <button type="submit" disabled={busy} className="btn btn-primary">
              建立活動
            </button>
            <span className="text-sub text-[13px]">
              共 {tableTotal} 桌 ＝ {capacity} 席
              {groups.length > 1 && (
                <span className="text-sub">
                  （{groups.map((g) => `${g.count}×${g.seats}`).join(" + ")}）
                </span>
              )}
            </span>
          </div>

          {error && <p className="tint tint-bad sm:col-span-2">{error}</p>}
          {createdCode && (
            <p className="tint tint-ok sm:col-span-2">
              ✓ 已建立 {createdCode} ——{" "}
              <Link href={`/flight/${createdCode}`} className="font-semibold underline">
                報名頁
              </Link>{" "}
              ·{" "}
              <Link href={`/admin/${createdCode}`} className="font-semibold underline">
                乘客名單
              </Link>
            </p>
          )}
        </form>
      )}
    </div>
  );
}

/**
 * 後台首頁的一列活動。
 *
 * 刪除的確認狀態放在這一列自己身上，而不是提到頁面層 ——
 * 每一列各自獨立，不會出現「展開了 A 的確認框、刪掉的卻是 B」。
 */
function FlightRow({ flight: f, counts }: BoardFlight) {
  const cap = flightCapacity(f);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 前端這道檢查只是體驗（讓按鈕在打對之前是灰的），真正的防線在後端
  const matched = typed.trim().toUpperCase() === f.code.toUpperCase();

  const close = () => {
    setConfirming(false);
    setTyped("");
    setError(null);
  };

  const remove = async () => {
    if (busy || !matched) return;
    setBusy(true);
    setError(null);
    const res = await deleteFlight(f.code, typed.trim());
    setBusy(false);
    if (!res.ok) {
      setError(
        res.error === "not-found"
          ? "這場活動已經不在了（可能在別的分頁刪過）。"
          : "刪除失敗，請再試一次。",
      );
      return;
    }
    // 成功不用收合：列表刷新後這一列會直接消失
  };

  return (
    <div className="card px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="text-[16px] font-bold">{f.title}</span>
            <span className="text-sub text-[13px]">{f.code}</span>
            <span className={`pill ${f.status === "open" ? "pill-ok" : "pill-bad"}`}>
              {f.status === "open" ? "開放中" : "已截止"}
            </span>
          </div>
          <div className="text-sub mt-1 text-[13px]">
            {fmtDate(f.departAt)} {fmtTime(f.departAt)} · {f.venueName} · 報名 {counts.confirmed}/{cap}
            {counts.standby > 0 && ` · 候補 ${counts.standby}`} · 已報到 {counts.boarded}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link href={`/flight/${f.code}`} className="btn btn-secondary px-3.5 py-2 text-[13px]">
            報名頁
          </Link>
          <Link href={`/admin/${f.code}`} className="btn btn-secondary px-3.5 py-2 text-[13px]">
            乘客名單
          </Link>
          <Link href={`/admin/${f.code}/boarding`} className="btn btn-secondary px-3.5 py-2 text-[13px]">
            登機口
          </Link>
          <button
            onClick={() => setFlightStatus(f.code, f.status === "open" ? "closed" : "open")}
            className={`btn px-3.5 py-2 text-[13px] ${f.status === "open" ? "btn-danger" : "btn-secondary"}`}
          >
            {f.status === "open" ? "截止報名" : "重新開放"}
          </button>
          <button
            onClick={() => (confirming ? close() : setConfirming(true))}
            className="btn btn-secondary text-sub px-3.5 py-2 text-[13px]"
            aria-expanded={confirming}
          >
            {confirming ? "取消刪除" : "刪除"}
          </button>
        </div>
      </div>

      {confirming && (
        <div className="border-line mt-4 border-t pt-4">
          <p className="text-[14px] font-semibold">確定要刪除「{f.title}」？</p>
          {/*
            把「會失去什麼」講清楚再要求確認。主辦人記得的是活動名稱，
            不是這場已經有多少人報名 —— 那才是真正回不來的東西。
          */}
          <p className="text-sub mt-1 text-[13px]">
            這個動作無法復原。
            {counts.confirmed > 0 || counts.standby > 0 ? (
              <>
                {" "}
                已報名的 {counts.confirmed} 人
                {counts.standby > 0 && `、候補 ${counts.standby} 人`}
                、他們選好的座位與已發出的登機證，還有接龍名單與桌邊聊天都會一起消失。
              </>
            ) : (
              " 這場活動還沒有人報名。"
            )}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value.toUpperCase())}
              className="field-input w-44"
              placeholder={f.code}
              aria-label={`輸入活動代號 ${f.code} 以確認刪除`}
              autoFocus
            />
            <button
              onClick={remove}
              disabled={!matched || busy}
              className="btn btn-danger px-3.5 py-2 text-[13px] disabled:opacity-40"
            >
              {busy ? "刪除中…" : "確認刪除"}
            </button>
            <span className="text-sub text-[13px]">
              請輸入活動代號 <span className="font-semibold">{f.code}</span> 以確認
            </span>
          </div>
          {error && <p className="tint tint-bad mt-3">{error}</p>}
        </div>
      )}
    </div>
  );
}

/** 主辦人後台首頁：活動列表 + 開新活動 */
export default function AdminPage() {
  const board = useBoard();

  return (
    <BoardShell wide>
      <BoardHeader sub="主辦人後台" />
      <AdminGate>
        <SectionTitle en="FLIGHTS" zh="活動管理" />

        {!board ? (
          <Loading />
        ) : (
          <div className="flex flex-col gap-3">
            {[...board]
              .sort((a, b) => new Date(b.flight.departAt).getTime() - new Date(a.flight.departAt).getTime())
              .map((row) => (
                <FlightRow key={row.flight.id} {...row} />
              ))}
            {board.length === 0 && (
              <div className="card text-sub px-5 py-10 text-center text-[14px]">還沒有活動，往下開第一場吧。</div>
            )}
          </div>
        )}

        <CreateFlightForm />
      </AdminGate>
    </BoardShell>
  );
}
